import React, { useState } from "react";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../components/Common/CustomTable";
import useApi from "../../Hooks/useApi";
import { getLoanList } from "../../helpers/API";
import useFilter from "../../Hooks/useFilter";
import { DATE_FORMAT } from "../../helpers/dateFormat";

const List = ({ t }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useFilter({});
  const [statusFilter, setStatusFilter] = useState("all");

  const [data] = useApi({
    callback: getLoanList,
    payload: { ...filters, status: statusFilter !== "all" ? statusFilter : undefined },
    dependencies: [filters, statusFilter],
  });

  const statusFilters = [
    { key: "all", label: t("customer.list.filters.all"), variant: "primary" },
    { key: "active", label: t("customer.list.filters.loanActive"), variant: "warning" },
    { key: "completed", label: t("customer.list.filters.loanFullyPaid"), variant: "success" },
  ];

  const columns = [
    {
      name: t("customer.list.columns.loanDate"),
      dateField: "loanStartDate",
      sortable: true,
      sortField: "loanStartDate",
      dateFormat: DATE_FORMAT.DISPLAY,
      width: "120px",
    },
    {
      name: t("customer.list.columns.billNo"),
      selector: (row) => (
        <span className="fw-semibold text-primary">{row.loanNumber}</span>
      ),
      sortable: true,
      sortField: "loanNumber",
      width: "200px",
    },
    {
      name: t("customer.list.columns.name"),
      selector: (row) => (
        <span className="fw-medium">{row.name}</span>
      ),
      sortable: true,
      sortField: "name",
    },
    {
      name: t("customer.list.columns.mobileNo"),
      selector: (row) => row.mobileNumber || "—",
      sortable: true,
      sortField: "mobileNumber",
      width: "140px",
    },
    {
      name: t("customer.list.columns.loanAmount"),
      selector: (row) => (
        <span className="fw-semibold">
          ₹{Number(row.loanAmount || 0).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
      sortField: "loanAmount",
      width: "140px",
      left: true,
    },
    {
      name: t("customer.list.columns.totalInterest"),
      selector: (row) => (
        <span className="fw-semibold">
          ₹{Number(row.totalInterest || 0).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
      sortField: "totalInterest",
      width: "140px",
      left: true,
    },
    {
      name: t("customer.list.columns.balanceAmount"),
      selector: (row) => {
        const balance = (Number(row.totalAmountPayable || 0) - (Number(row.receivedPrinciple || 0) + Number(row.receivedInterest || 0)));
        return (
          <span className={`fw-semibold ${balance > 0 ? "text-danger" : "text-success"}`}>
            ₹{balance > 0 ? balance.toLocaleString("en-IN") : 0}
          </span>
        );
      },
      sortable: true,
      sortField: "balanceAmount",
      width: "150px",
      left: true,
    },
    {
      name: t("customer.list.columns.maturityDate"),
      dateField: "loanEndDate",
      sortable: true,
      sortField: "loanEndDate",
      dateFormat: DATE_FORMAT.DISPLAY,
      width: "130px",
    },
    {
      name: t("customer.list.columns.loanStatus"),
      selector: (row) => {
        const status = row.status || "active";
        const isFullyPaid = status === "completed";
        return (
          <span
            className={`badge ${isFullyPaid ? "bg-success" : "bg-warning text-dark"} rounded-pill px-2 py-1`}
            style={{ fontSize: "11px" }}
          >
            <i className={`bx ${isFullyPaid ? "bx-check-circle" : "bx-time-five"} me-1`}></i>
            {isFullyPaid
              ? t("customer.list.filters.loanFullyPaid")
              : t("customer.list.filters.loanActive")}
          </span>
        );
      },
      sortable: true,
      sortField: "loanStatus",
      width: "150px",
      center: true,
    },
    {
      name: t("customer.list.columns.actions"),
      actionItems: [
        {
          label: t("customer.list.actions.view"),
          icon: "ri-eye-fill",
          className: "btn btn-outline-primary",
          onClick: (data) => {
            navigate(`/customer/view/${data.id}`);
          },
        },
      ],
      width: "80px",
      center: true,
    },
  ];

  // Filter buttons rendered inside the table header
  const filterButtons = (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="text-muted small fw-medium me-1">
        {t("customer.list.filters.filterBy")}:
      </span>
      {statusFilters.map(({ key, label, variant }) => (
        <button
          key={key}
          type="button"
          className={`btn btn-sm ${statusFilter === key
            ? `btn-${variant} shadow-sm`
            : `btn-outline-${variant}`
            }`}
          style={{ fontSize: "12px", padding: "4px 12px" }}
          onClick={() => setStatusFilter(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <CustomTable
            columns={columns}
            data={data}
            buttonAction={() => {
              navigate("/customer/add");
            }}
            buttonLabel={t("customer.list.button.addNew")}
            buttonIcon="bx bx-user-plus"
            onStatusChange={(data) => setFilters(data)}
            searchKeys={["loanNumber", "borrowerName"]}
            searchPlaceholder={t("customer.list.search.placeholder")}
            headerContent={filterButtons}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default withTranslation()(List);
